import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppShell,
  Group,
  Title,
  Anchor,
  Button,
  Container,
  Text,
  Stack,
  Divider,
  ActionIcon,
  Tooltip,
  Box,
  Menu,
  Burger,
  Drawer,
  Avatar,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMantineColorScheme, useComputedColorScheme } from '@mantine/core';
import {
  IconBooks,
  IconHeart,
  IconSun,
  IconMoon,
  IconUserCircle,
  IconLogout,
  IconSearch,
  IconBuildingChurch,
  IconBookmarks,
  IconBook,
  IconChevronDown,
} from '@tabler/icons-react';
import { AISearchBar } from '@/components/AISearchBar';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner';

/**
 * Public-facing layout for anonymous-first browsing.
 *
 * Layout strategy:
 *  - Single 60px header. Brand on the left, primary nav in the centre on
 *    desktop, action cluster on the right.
 *  - On screens < md, the centre nav collapses into a right-side Drawer that
 *    a burger button toggles. The brand collapses to "Acts" only.
 *  - The user state lives in one Menu (Account / theme / sign out) so the
 *    mobile and desktop affordances stay identical — no parallel button
 *    clusters competing for room.
 */
export function PublicLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, signOut } = useOptionalAuth();
  const { user } = useEnrichedAuth();
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');
  const [mobileNavOpened, mobileNav] = useDisclosure(false);

  const toggle = () =>
    setColorScheme(computedColorScheme === 'light' ? 'dark' : 'light');

  // The "My Account" entry is only relevant once the viewer has a profile,
  // so it's appended conditionally based on `isAuthenticated`. Anon viewers
  // see the Sign-in button on the right cluster instead.
  const navItems: { to: string; label: string; icon: ReactNode }[] = [
    { to: '/courses', label: 'Courses', icon: <IconBook size={16} /> },
    { to: '/ministries', label: 'Ministries', icon: <IconBuildingChurch size={16} /> },
    { to: '/kb', label: 'Knowledge Base', icon: <IconBookmarks size={16} /> },
    { to: '/search', label: 'Search', icon: <IconSearch size={16} /> },
    ...(isAuthenticated
      ? [{ to: '/account', label: 'My Account', icon: <IconUserCircle size={16} /> }]
      : []),
  ];

  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  const userInitials = (() => {
    const name = `${(user as { firstName?: string } | null)?.firstName ?? ''} ${(user as { surname?: string } | null)?.surname ?? ''}`.trim();
    if (!name) return 'U';
    return name
      .split(/\s+/)
      .map((n) => n[0]?.toUpperCase() ?? '')
      .slice(0, 2)
      .join('');
  })();

  const userMenu = (
    <Menu position="bottom-end" withinPortal width={220} shadow="md">
      <Menu.Target>
        <UnstyledButton
          aria-label="Account menu"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 6px 4px 4px',
            borderRadius: 999,
            border: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Avatar size={28} radius="xl" color="brand" variant="light">
            {userInitials}
          </Avatar>
          <Box visibleFrom="lg">
            <Text size="sm" fw={500} lineClamp={1} style={{ maxWidth: 140 }}>
              {(user as { firstName?: string } | null)?.firstName ?? 'Account'}
            </Text>
          </Box>
          <IconChevronDown size={14} style={{ marginRight: 2 }} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconUserCircle size={14} />} component={Link} to="/account">
          My account
        </Menu.Item>
        <Menu.Item
          leftSection={computedColorScheme === 'light' ? <IconMoon size={14} /> : <IconSun size={14} />}
          onClick={toggle}
        >
          {computedColorScheme === 'light' ? 'Dark mode' : 'Light mode'}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item leftSection={<IconLogout size={14} />} c="red" onClick={signOut}>
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <AppShell header={{ height: 60 }} footer={{ height: 96 }} padding="lg">
      <AppShell.Header>
        <Container size="xl" h="100%" px={{ base: 'sm', md: 'md' }}>
          <Group h="100%" justify="space-between" wrap="nowrap" gap="sm">
            {/* Brand */}
            <UnstyledButton
              onClick={() => navigate('/')}
              aria-label="Home"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              <IconBooks size={26} color="var(--mantine-color-brand-6)" style={{ flex: '0 0 auto' }} />
              <Title
                order={5}
                lh={1.1}
                fw={700}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  letterSpacing: '-0.01em',
                }}
              >
                <Box component="span" visibleFrom="sm">Acts Resource Centre</Box>
                <Box component="span" hiddenFrom="sm">Acts</Box>
              </Title>
            </UnstyledButton>

            {/* Centre nav (desktop only) */}
            <Group gap="xs" visibleFrom="md" wrap="nowrap">
              {navItems.map((item) => (
                <Anchor
                  key={item.to}
                  component={Link}
                  to={item.to}
                  px={10}
                  py={6}
                  underline="never"
                  c={isActive(item.to) ? 'brand.7' : 'var(--mantine-color-text)'}
                  fw={isActive(item.to) ? 600 : 500}
                  style={{
                    fontSize: 14,
                    borderRadius: 8,
                    background: isActive(item.to) ? 'var(--mantine-color-brand-light)' : 'transparent',
                    transition: 'background 120ms ease',
                  }}
                >
                  {item.label}
                </Anchor>
              ))}
            </Group>

            {/* Right cluster */}
            <Group gap="xs" wrap="nowrap" style={{ flex: '0 0 auto' }}>
              {/* Theme toggle — desktop standalone, on mobile lives inside the user menu / drawer. */}
              <Tooltip label={computedColorScheme === 'light' ? 'Dark mode' : 'Light mode'}>
                <ActionIcon
                  variant="subtle"
                  onClick={toggle}
                  aria-label="Toggle color scheme"
                  visibleFrom="sm"
                  size="lg"
                >
                  {computedColorScheme === 'light' ? <IconMoon size={18} /> : <IconSun size={18} />}
                </ActionIcon>
              </Tooltip>

              {isAuthenticated ? (
                userMenu
              ) : (
                <Button
                  component={Link}
                  to={`/auth/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
                  variant="filled"
                  size="sm"
                >
                  Sign in
                </Button>
              )}

              {/* Mobile burger — only renders below md. */}
              <Burger
                opened={mobileNavOpened}
                onClick={mobileNav.toggle}
                size="sm"
                hiddenFrom="md"
                aria-label={mobileNavOpened ? 'Close navigation' : 'Open navigation'}
              />
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      {/* Mobile nav drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={mobileNav.close}
        position="right"
        size="82%"
        padding="md"
        title={
          <Group gap="xs">
            <IconBooks size={20} color="var(--mantine-color-brand-6)" />
            <Text fw={700}>Acts Resource Centre</Text>
          </Group>
        }
        hiddenFrom="md"
      >
        <Stack gap="md">
          <AISearchBar compact />
          <Stack gap={4}>
            {navItems.map((item) => (
              <Button
                key={item.to}
                component={Link}
                to={item.to}
                variant={isActive(item.to) ? 'light' : 'subtle'}
                color="brand"
                justify="flex-start"
                leftSection={item.icon}
                size="md"
                onClick={mobileNav.close}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Divider />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Theme</Text>
            <Button
              variant="default"
              size="xs"
              leftSection={
                computedColorScheme === 'light' ? <IconMoon size={14} /> : <IconSun size={14} />
              }
              onClick={toggle}
            >
              {computedColorScheme === 'light' ? 'Dark mode' : 'Light mode'}
            </Button>
          </Group>
          {!isAuthenticated && (
            <Button
              component={Link}
              to={`/auth/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
              variant="filled"
              size="md"
              fullWidth
              onClick={mobileNav.close}
            >
              Sign in
            </Button>
          )}
        </Stack>
      </Drawer>

      <AppShell.Main>
        <EmailConfirmationBanner />
        <Container size="xl" py="lg">{children}</Container>
      </AppShell.Main>

      {/* Sticky footer — pinned to the bottom of the viewport regardless of
          how short the page content is. Mantine's AppShell.Footer is sticky
          by default, but we add explicit position+border so it reads as a
          distinct band on every page (short and long alike). */}
      <AppShell.Footer
        withBorder
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--mantine-color-body)',
          zIndex: 100,
        }}
      >
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between" align="center" wrap="wrap">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Made with <IconHeart size={10} style={{ color: '#e74c3c', verticalAlign: 'middle' }} /> by Acts Christian Church
              </Text>
              <Text size="xs" c="dimmed">
                © {new Date().getFullYear()} Acts Christian Church — all rights reserved.
              </Text>
            </Stack>
            <Group gap="lg" visibleFrom="sm">
              <Anchor component={Link} to="/courses" size="xs" c="dimmed">Courses</Anchor>
              <Anchor component={Link} to="/ministries" size="xs" c="dimmed">Ministries</Anchor>
              <Divider orientation="vertical" />
              <Anchor component={Link} to="/kb" size="xs" c="dimmed">Knowledge Base</Anchor>
              <Divider orientation="vertical" />
              <Anchor component={Link} to="/search" size="xs" c="dimmed">Search</Anchor>
            </Group>
          </Group>
        </Container>
      </AppShell.Footer>
    </AppShell>
  );
}
